import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {Timestamp} from 'firebase/firestore';
import {useCallback, useMemo, useState} from 'react';
import {FormattedDate} from 'react-intl';
import {Loading} from '../common/Loading';
import {Translation} from '../i18n/Translation';
import {ComponentWithoutChildren} from '../react/types';
import {useSignupRuleCollection} from './__hooks__/useSignupRuleCollection';
import {useSignupRuleDocument} from './__hooks__/useSignupRuleDocument';
import {SignupRule} from '#common/auth/rules';
import {SignupRuleType} from '#common/firebase/collections';
import {EntityWithMetadata, MetadataField} from '#common/models/Entity';
import {hasPermission} from '#common/users/permissions';
import {User} from '#common/users/types';

export interface SignupRuleListProps {
  type: SignupRuleType;
  user: User;
}

type Order = 'asc' | 'desc';
type SortableProperty = keyof SignupRule | 'createdTime' | 'updatedTime';

export const SignupRuleList: ComponentWithoutChildren<SignupRuleListProps> = ({type, user}: SignupRuleListProps) => {
  const collection = useSignupRuleCollection(type);
  const {data: rules, status} = collection.observe();
  const [orderBy, setOrderBy] = useState<SortableProperty>('value');
  const [order, setOrder] = useState<Order>('asc');

  const canList = useMemo(() => hasPermission(user, 'list:signup.rule'), [user]);

  const handleRequestSort = (property: SortableProperty) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRules = useMemo(() => {
    if (!rules) return [];
    return [...rules].sort((a, b) => {
      let valA: unknown = a[orderBy as keyof SignupRule];
      let valB: unknown = b[orderBy as keyof SignupRule];

      if (orderBy === 'createdTime') {
        valA = a[MetadataField]?.createdTime ?? 0;
        valB = b[MetadataField]?.createdTime ?? 0;
      } else if (orderBy === 'updatedTime') {
        valA = a[MetadataField]?.updatedTime ?? 0;
        valB = b[MetadataField]?.updatedTime ?? 0;
      }

      const aComp = (valA as number | string | Date | Timestamp) ?? '';
      const bComp = (valB as number | string | Date | Timestamp) ?? '';

      if (bComp < aComp) return order === 'asc' ? 1 : -1;
      if (bComp > aComp) return order === 'asc' ? -1 : 1;
      return 0;
    });
  }, [rules, order, orderBy]);

  if (status === 'loading') {
    return (
      <Box sx={{alignItems: 'center', display: 'flex', flexGrow: 1, justifyContent: 'center'}}>
        <Loading />
      </Box>
    );
  }

  if (!canList) {
    return null;
  }

  if (rules?.length === 0) {
    return (
      <Translation keys="user:administration.rules.none">
        {(text) => (
          <Typography sx={{p: 4, textAlign: 'center'}} variant="body2">
            {text}
          </Typography>
        )}
      </Translation>
    );
  }

  return (
    <TableContainer sx={{flexGrow: 1}}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={orderBy === 'value' ? order : false}>
              <TableSortLabel
                active={orderBy === 'value'}
                direction={orderBy === 'value' ? order : 'asc'}
                onClick={() => handleRequestSort('value')}
              >
                <Translation keys="user:administration.rules.column.value">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'type' ? order : false}>
              <TableSortLabel
                active={orderBy === 'type'}
                direction={orderBy === 'type' ? order : 'asc'}
                onClick={() => handleRequestSort('type')}
              >
                <Translation keys="user:administration.rules.column.type">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'description' ? order : false}>
              <TableSortLabel
                active={orderBy === 'description'}
                direction={orderBy === 'description' ? order : 'asc'}
                onClick={() => handleRequestSort('description')}
              >
                <Translation keys="user:administration.rules.column.description">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sortDirection={orderBy === 'enabled' ? order : false}>
              <TableSortLabel
                active={orderBy === 'enabled'}
                direction={orderBy === 'enabled' ? order : 'asc'}
                onClick={() => handleRequestSort('enabled')}
              >
                <Translation keys="user:administration.rules.column.enabled">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'createdTime' ? order : false}>
              <TableSortLabel
                active={orderBy === 'createdTime'}
                direction={orderBy === 'createdTime' ? order : 'asc'}
                onClick={() => handleRequestSort('createdTime')}
              >
                <Translation keys="user:administration.rules.column.created">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'updatedTime' ? order : false}>
              <TableSortLabel
                active={orderBy === 'updatedTime'}
                direction={orderBy === 'updatedTime' ? order : 'asc'}
                onClick={() => handleRequestSort('updatedTime')}
              >
                <Translation keys="user:administration.rules.column.updated">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <Translation keys="user:administration.rules.column.actions">{(text) => <>{text}</>}</Translation>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRules.map((rule) => (
            <SignupRuleItem key={rule.id} rule={rule} type={type} user={user} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface SignupRuleItemProps {
  rule: SignupRule;
  type: SignupRuleType;
  user: User;
}

const SignupRuleItem: ComponentWithoutChildren<SignupRuleItemProps> = ({rule, type, user}: SignupRuleItemProps) => {
  const document = useSignupRuleDocument(type, rule.id);

  const canUpdate = useMemo(() => hasPermission(user, 'update:signup.rule'), [user]);
  const canDelete = useMemo(() => hasPermission(user, 'delete:signup.rule'), [user]);

  const toggleEnabled = useCallback(() => {
    void document.patch({enabled: !rule.enabled});
  }, [document, rule.enabled]);

  const removeRule = useCallback(() => {
    void document.delete();
  }, [document]);

  const createdDate = useMemo(() => {
    const time: unknown = (rule as EntityWithMetadata<SignupRule>)[MetadataField]?.createdTime;
    if (time instanceof Date) {
      return time;
    }
    if (time instanceof Timestamp) {
      return time.toDate();
    }
    if (typeof time === 'number' && time > 0) {
      return new Date(time);
    }
    return null;
  }, [rule]);

  const updatedDate = useMemo(() => {
    const time: unknown = (rule as EntityWithMetadata<SignupRule>)[MetadataField]?.updatedTime;
    if (time instanceof Date) {
      return time;
    }
    if (time instanceof Timestamp) {
      return time.toDate();
    }
    if (typeof time === 'number' && time > 0) {
      return new Date(time);
    }
    return null;
  }, [rule]);

  return (
    <TableRow hover>
      <TableCell>{rule.value}</TableCell>
      <TableCell>{rule.type}</TableCell>
      <TableCell>{rule.description || '-'}</TableCell>
      <TableCell align="center">
        <Translation keys="user:administration.rules.action.toggle">
          {(text) => (
            <Tooltip title={text}>
              <Switch checked={rule.enabled} disabled={!canUpdate} onChange={toggleEnabled} size="small" />
            </Tooltip>
          )}
        </Translation>
      </TableCell>
      <TableCell>
        {createdDate ? <FormattedDate dateStyle="short" timeStyle="short" value={createdDate} /> : '-'}
      </TableCell>
      <TableCell>
        {updatedDate ? <FormattedDate dateStyle="short" timeStyle="short" value={updatedDate} /> : '-'}
      </TableCell>
      <TableCell align="right">
        {canDelete && (
          <Translation keys="user:administration.rules.action.delete">
            {(text) => (
              <Tooltip title={text}>
                <IconButton onClick={removeRule} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Translation>
        )}
      </TableCell>
    </TableRow>
  );
};
